import { onScopeDispose, watch } from "vue";

/**
 * Android Chrome only fills an SMS code into the page through the WebOTP API,
 * autocomplete="one-time-code" alone does nothing there (iOS Safari handles that
 * attribute on its own, so the API stays unsupported and unused on iOS).
 *
 * The SMS has to end with a line containing only the origin and the code, e.g.
 * "@bnpl.instacash.hu #123456", otherwise the browser never hands the code over.
 *
 * @param onCode called with the received code, may be async
 * @param isActive optional ref/getter, the code is only listened for while it is truthy
 */
export const useWebOtp = (onCode, isActive = null) => {
  let abortController = null;

  function isSupported() {
    return (
      typeof window !== "undefined" &&
      window.isSecureContext &&
      "OTPCredential" in window &&
      !!navigator.credentials
    );
  }

  function cancel() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  }

  async function listen() {
    if (!isSupported()) {
      return;
    }
    // only one request can be pending at a time
    cancel();

    const controller = new AbortController();
    abortController = controller;

    try {
      const credential = await navigator.credentials.get({
        otp: { transport: ["sms"] },
        signal: controller.signal,
      });
      if (credential && credential.code) {
        await onCode(credential.code);
      }
    } catch (error) {
      // cancelling the request (leaving the step, unmounting) also lands here
      if (error.name !== "AbortError") {
        console.log("WebOTP hiba: " + error);
      }
    } finally {
      if (abortController === controller) {
        abortController = null;
      }
    }
  }

  if (isActive) {
    watch(
      isActive,
      (active) => {
        if (active) {
          listen();
        } else {
          cancel();
        }
      },
      { immediate: true }
    );
  }

  onScopeDispose(cancel);

  return { listen, cancel };
};
